import React from "react";
import {
  DropdownInput,
  IOption,
  QuestionTooltipProps,
  QuestionTooltip,
} from "component-library";

import { IAgency } from "models";

import "./AddAgencyForm.scss";
import { dataTestIds } from "data-testids";

export interface IAddAgencyFormContent {
  agencyNameFieldLabel: string;
  agentIsNotInTheListLabel: string;
  validationMessage: string;
  agencyDropdownPlaceholder: string;
  agencySearchPlaceholder: string;
  addAgentTooltip: QuestionTooltipProps;
}

export const IAddAgencyFormContentKeys = [
  "agencyNameFieldLabel",
  "agentIsNotInTheListLabel",
  "validationMessage",
  "agencyDropdownPlaceholder",
  "agencySearchPlaceholder",
  "addAgentTooltip",
];

export interface IAddAgencyFormProps {
  content: IAddAgencyFormContent;
  agencies: IAgency[];
  selectAgency: (item: IAgency) => void;
  addNewAgency: () => void;
  isAgencyFormInvalid: boolean;
}

interface IAddAgencyFormState {
  agenciesList: IOption<IAgency>[];
}

export class AddAgencyForm extends React.Component<
  IAddAgencyFormProps,
  IAddAgencyFormState
> {
  public readonly state: Readonly<IAddAgencyFormState> = {
    agenciesList: [],
  };

  constructor(props: Readonly<IAddAgencyFormProps>) {
    super(props);

    this.state = {
      agenciesList: AddAgencyForm.mapAgencies(props.agencies),
    };
  }

  public render() {
    const { content, isAgencyFormInvalid } = this.props;
    const { agenciesList } = this.state;

    const searchInputProps = {
      show: true,
      placeholder: content.agencySearchPlaceholder,
    };

    const compareFn = (left: IAgency, right: IAgency) =>
      left.refNumber === right.refNumber;

    return (
      <div className="c-add-agency-form">
        <div className={"c-add-agency-form__input"}>
          <DropdownInput
            name="agencies"
            id="addAgenciesFormAgencies"
            variant="select"
            placeholder={content.agencyDropdownPlaceholder}
            options={agenciesList}
            invalid={isAgencyFormInvalid}
            validationMessage={content.validationMessage}
            valueChanged={this.valueChanged}
            valueComparer={compareFn}
            truncateOptions
            searchInput={searchInputProps}
            label={content.agencyNameFieldLabel}
            annex={this.getTooltipProps(content.addAgentTooltip)}
            data-testid={dataTestIds.profile["AddAgency.AgencyFilter.dropdown"]}
          />
        </div>
        <div className="c-add-agency-form__control">
          <a
            className="g-alternative-link g-alternative-link--lg"
            onClick={this.addNewAgency}
            onKeyPress={(e) => e.key === "Enter" && this.addNewAgency(e)}
            tabIndex={0}
          >
            {content.agentIsNotInTheListLabel}
          </a>
        </div>
      </div>
    );
  }

  public static getDerivedStateFromProps(
    nextProps: IAddAgencyFormProps,
    prevState: IAddAgencyFormState
  ) {
    let stateUpdates = {};

    if (nextProps.agencies.length !== prevState.agenciesList.length) {
      const agenciesList = AddAgencyForm.mapAgencies(nextProps.agencies);
      stateUpdates = { agenciesList };
    }

    return stateUpdates;
  }

  private getTooltipProps = (content: QuestionTooltipProps) => {
    return content && <QuestionTooltip {...content} />;
  };

  private addNewAgency = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.addNewAgency();
  };

  private valueChanged = (value: IAgency) => {
    this.props.selectAgency(value);

    return Promise.resolve();
  };

  private static mapAgencies = (agencies: IAgency[]): IOption<IAgency>[] =>
    agencies.map((agency) => ({
      value: agency,
      name: `${agency.name} ${agency.email}`,
    }));
}
